import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Container, Paper } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { LangContext } from '../landing';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

/**
 * Page de remerciement après un paiement réussi
 * Cette page est affichée après que l'utilisateur ait effectué un paiement avec succès
 * Elle permet également le tracking des conversions via Google Tag Manager
 */
export const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { lang } = React.useContext(LangContext);
  
  // Récupérer le sessionId de l'URL si présent
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    // Tracking Google Tag Manager pour la conversion
    if (window && window.dataLayer && sessionId) {
      window.dataLayer.push({
        event: 'purchase_success',
        ecommerce: {
          transaction_id: sessionId,
          value: 99.00, // Valeur de la transaction
          currency: 'EUR',
          items: [{
            item_name: 'Abonnement TacticaFoot',
            item_id: 'tacticafoot-subscription',
            price: 99.00,
            quantity: 1
          }]
        }
      });
      
      console.log('Événement de conversion envoyé à GTM:', sessionId);
    }
    
    // Si l'utilisateur n'est pas connecté, rediriger vers la page d'accueil après 5 secondes
    if (!auth.user) {
      const timer = setTimeout(() => {
        navigate('/');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [sessionId, auth.user, navigate]);
  
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
            {lang === 'fr' ? 'Paiement réussi !' : 'Payment Successful!'}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            {lang === 'fr' 
              ? 'Merci pour votre abonnement à TacticaFoot. Votre compte a été mis à jour avec succès.'
              : 'Thank you for your TacticaFoot subscription. Your account has been successfully updated.'}
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary' }}>
            {lang === 'fr'
              ? 'Vous recevrez prochainement un email de confirmation avec tous les détails.'
              : 'You will soon receive a confirmation email with all the details.'}
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => navigate('/tutorial')}
              sx={{ px: 3, py: 1 }}
            >
              {lang === 'fr' ? 'Accéder au tutoriel' : 'Go to tutorial'}
            </Button>
            
            <Button 
              variant="outlined" 
              color="primary"
              onClick={() => navigate('/')}
              sx={{ px: 3, py: 1 }}
            >
              {lang === 'fr' ? 'Retour à l\'accueil' : 'Back to home'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PaymentSuccessPage;
